import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Lock, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <div className="grid-bg min-h-[calc(100vh-73px)] flex items-center">
        <div className="max-w-md w-full mx-auto px-6 py-16">
          <div className="card-surface p-8 text-center">
            <AlertTriangle className="mx-auto text-[#EF4444] mb-4" size={44} />
            <h1 className="font-display font-extrabold text-2xl tracking-tighter mb-2">
              Invalid reset link
            </h1>
            <p className="text-zinc-400 text-sm mb-6">
              The link is missing a token. Request a new one.
            </p>
            <Link to="/forgot-password" className="btn-bid text-sm">
              Get a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid-bg min-h-[calc(100vh-73px)] flex items-center">
      <div className="max-w-md w-full mx-auto px-6 py-16">
        <h1 className="font-display font-extrabold text-4xl tracking-tighter mb-2">
          Choose a new password
        </h1>
        <p className="text-zinc-500 text-sm mb-8">
          Pick something you can remember — minimum 6 characters.
        </p>
        <form onSubmit={submit} className="card-surface p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
              New password
            </label>
            <input
              type="password"
              required
              minLength={6}
              className="input-field"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
              Confirm new password
            </label>
            <input
              type="password"
              required
              minLength={6}
              className="input-field"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-bid w-full text-sm flex items-center justify-center gap-2"
            data-testid="reset-submit-button"
          >
            <Lock size={14} />
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
